import { fetchData } from './dataFetching';
import { transformData } from './dataTransformation';

const holeColumns = {
  'Hole No': 'Hole_ID',
  'X': 'NAT_East',
  'Y': 'NAT_North',
  'Z': 'NAT_RL',
  'Azimuth': 'Collar_Azimuth',
  'Dip': 'Collar_Dip',
  'Depth': 'Max_Depth',
};

const assayColumns = {
  'HoleID': 'Hole_ID',
  'from': 'Depth_From',
  'to': 'Depth_To',
  'grade': 'Au_ppm',
};

//const constants = [259333, 1120, 8156513];
const constants = [789000, 1000, 7970500];

class HoleDataCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  async getData(hole_url, assay_url, survey_url = null) {
    const cacheKey = `${hole_url}_${assay_url || 'no_assay'}_${survey_url || 'no_survey'}`;
    
    // Return cached data if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Start loading data
    const loadingPromise = this.loadData(hole_url, assay_url, survey_url);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const data = await loadingPromise;
      this.cache.set(cacheKey, data);
      this.loadingPromises.delete(cacheKey);
      return data;
    } catch (error) {
      this.loadingPromises.delete(cacheKey);
      throw error;
    }
  }

  async loadData(hole_url, assay_url, survey_url = null) {
    const data = await fetchData(hole_url, assay_url, holeColumns, assayColumns, survey_url);
    const transformed = transformData(data, holeColumns, assayColumns, constants);
    return transformed;
  }

  // Get filtered data for specific dataset and hole type
  getFilteredData(hole_url, assay_url, dataset, holeType, year = [], programType = [], survey_url = null) {
    const cacheKey = `${hole_url}_${assay_url || 'no_assay'}_${survey_url || 'no_survey'}`;
    const allData = this.cache.get(cacheKey);
    
    if (!allData) {
      return null;
    }

    // Filter the data based on dataset and hole type
    const filteredData = {};
    Object.keys(allData).forEach(holeId => {
      const holeData = allData[holeId];
      
      // If dataset/holeType aren't provided (collar-only quick load), don't filter them out.
      const datasetMatches = dataset ? holeData.data.DataSet === dataset : true;
      const holeTypeMatches = holeType ? holeData.data.Hole_Type === holeType : true;

      if (datasetMatches && holeTypeMatches) {
        // Check if all coordinate fields are filled in and convertible to double
        const hasValidCoordinates = holeData.data.NAT_East && 
                                   holeData.data.NAT_North && 
                                   holeData.data.NAT_RL &&
                                   !isNaN(parseFloat(holeData.data.NAT_East)) &&
                                   !isNaN(parseFloat(holeData.data.NAT_North)) &&
                                   !isNaN(parseFloat(holeData.data.NAT_RL)) &&
                                   isFinite(parseFloat(holeData.data.NAT_East)) &&
                                   isFinite(parseFloat(holeData.data.NAT_North)) &&
                                   isFinite(parseFloat(holeData.data.NAT_RL));
        
        if (!hasValidCoordinates) {
          return; // Skip holes without valid coordinates
        }
        
        // Apply year filtering if year array is provided and not empty
        let yearMatch = true;
        if (year && year.length > 0 && holeData.data.Year_Drilled) {
          yearMatch = year.includes(holeData.data.Year_Drilled);
        }
        
        // Apply program type filtering if programType array is provided and not empty
        let programTypeMatch = true;
        if (programType && programType.length > 0 && holeData.data.Program_Type) {
          programTypeMatch = programType.includes(holeData.data.Program_Type);
        }
        
        // Include hole if all filters match
        if (yearMatch && programTypeMatch) {
          filteredData[holeId] = holeData;
        }
      }
    });

    return filteredData;
  }

  // Clear cache if needed
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }
}

// Export singleton instance
export const holeDataCache = new HoleDataCache();
